import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { PhCalculator, PhMoney, PhToggleRight } from 'ember-phosphor-icons';
import SectionPanel from 'tio-ui/components/section-panel';
import { getFieldValue } from '../../../utils/tas/program-builder/sections';
import { money } from '../../../utils/tas/program-builder/summary';
import type IntlService from 'ember-intl/services/intl';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

type FieldsSignature = TASProgramTemplateModel['fields'];

/** SNAKE_CAPS enum name → Title Case (matches the builder's employment-type display). */
function humanizeEnumName(name: string): string {
  return name
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface LimitRow {
  scope: string;
  annual: string;
  lifetime: string;
}

interface FiscalYearRow {
  year: string;
  scope: string;
  annual: string;
}

interface FlagRow {
  name: string;
  label: string;
  on: boolean;
  /** Localized Yes/No — booleans are real answers, never em dashes. */
  state: string;
  /** Dependent setting shown indented under an enabled flag (e.g. a rate);
   * empty strings mean the flag has none (or is off). */
  detailName: string;
  detailLabel: string;
  detailValue: string;
}

interface ReadoutLimitsPanelSignature {
  Args: {
    fields: FieldsSignature;
    /** Reveal internal FIELD_NAMES badges (admin builder's devops toggle). */
    showFieldNames?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * Read-only Limits & Calculation: the budget caps as a real table (overall and
 * per-employment-type rows, plus fiscal-year overrides when configured), the
 * reimbursement-calculation methods with their rates, and the program options.
 * Caps render in credits when the program is credit-based, USD otherwise; an
 * em dash means no limit is set.
 */
export default class ReadoutLimitsPanel extends Component<ReadoutLimitsPanelSignature> {
  @service declare intl: IntlService;

  private str(name: string): string {
    return String(getFieldValue(this.args.fields, name) ?? '').trim();
  }

  private bool(name: string): boolean {
    return Boolean(getFieldValue(this.args.fields, name));
  }

  private obj(name: string): Record<string, unknown> {
    const raw = getFieldValue(this.args.fields, name);
    return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  }

  get creditMode(): boolean {
    const capacity = getFieldValue(this.args.fields, 'LIMIT_CAPACITY');
    return capacity === 'CREDITS' || capacity === true;
  }

  get capacityValue(): string {
    return this.creditMode
      ? this.intl.t('program_builder.summary.value.credit_based')
      : this.intl.t('program_builder.summary.value.dollar_based');
  }

  /** Stored cap → display: credits in credit mode, USD otherwise, '—' = no limit. */
  private cap(value: unknown): string {
    const raw = String(value ?? '').trim();
    if (!raw) return '—';
    return this.creditMode
      ? this.intl.t('program_builder.summary.value.credits', { count: Number(raw) })
      : money(value, this.intl.primaryLocale);
  }

  /** The editor preserves cleared inputs as `{ key: '' }` — an empty amount is
   * "not set", never a configured limit. */
  private hasAmount(value: unknown): boolean {
    return String(value ?? '').trim() !== '';
  }

  get limitRows(): LimitRow[] {
    const annualByType = this.obj('MAXIMUM_ANNUAL_REIMBURSEMENT_BY_EMPLOYMENT_TYPE');
    const lifetimeByType = this.obj('MAXIMUM_LIFETIME_REIMBURSEMENT_BY_EMPLOYMENT_TYPE');
    const types = [...new Set([...Object.keys(annualByType), ...Object.keys(lifetimeByType)])]
      .filter((type) => this.hasAmount(annualByType[type]) || this.hasAmount(lifetimeByType[type]))
      .sort();
    const rows: LimitRow[] = types.map((type) => ({
      scope: humanizeEnumName(type),
      annual: this.cap(annualByType[type]),
      lifetime: this.cap(lifetimeByType[type]),
    }));
    // The flat caps are the whole story without by-type rows; alongside them
    // they only appear when actually set (the builder swaps one for the other).
    const overall: LimitRow = {
      scope: this.intl.t('program_builder.readout.limits_all_employees'),
      annual: this.cap(getFieldValue(this.args.fields, 'MAXIMUM_ANNUAL_REIMBURSEMENT')),
      lifetime: this.cap(getFieldValue(this.args.fields, 'MAXIMUM_LIFETIME_REIMBURSEMENT')),
    };
    if (!rows.length || overall.annual !== '—' || overall.lifetime !== '—') {
      rows.unshift(overall);
    }
    return rows;
  }

  /** True when any cap exists anywhere — flat, per-employment-type, or fiscal-year. */
  get hasAnyLimit(): boolean {
    return (
      Boolean(this.str('MAXIMUM_ANNUAL_REIMBURSEMENT')) ||
      Boolean(this.str('MAXIMUM_LIFETIME_REIMBURSEMENT')) ||
      Object.values(this.obj('MAXIMUM_ANNUAL_REIMBURSEMENT_BY_EMPLOYMENT_TYPE')).some((value) =>
        this.hasAmount(value)
      ) ||
      Object.values(this.obj('MAXIMUM_LIFETIME_REIMBURSEMENT_BY_EMPLOYMENT_TYPE')).some((value) =>
        this.hasAmount(value)
      ) ||
      this.fiscalYearRows.length > 0
    );
  }

  /** Localized Yes/No for a boolean setting — a real answer, never an em dash. */
  private yesNo(on: boolean): string {
    return this.intl.t(
      on ? 'program_builder.summary.value.yes' : 'program_builder.summary.value.no'
    );
  }

  /** Fiscal-year annual overrides, flattened to year · scope · amount rows. */
  get fiscalYearRows(): FiscalYearRow[] {
    const rows: FiscalYearRow[] = [];
    for (const [year, amount] of Object.entries(
      this.obj('MAXIMUM_ANNUAL_REIMBURSEMENT_BY_FISCAL_YEAR')
    )) {
      if (!this.hasAmount(amount)) continue;
      rows.push({
        year,
        scope: this.intl.t('program_builder.readout.limits_all_employees'),
        annual: this.cap(amount),
      });
    }
    const byTypeByYear = this.obj(
      'MAXIMUM_ANNUAL_REIMBURSEMENT_BY_EMPLOYMENT_TYPE_BY_FISCAL_YEAR'
    ) as Record<string, Record<string, unknown> | undefined>;
    for (const [type, years] of Object.entries(byTypeByYear)) {
      for (const [year, amount] of Object.entries(years ?? {})) {
        if (!this.hasAmount(amount)) continue;
        rows.push({ year, scope: humanizeEnumName(type), annual: this.cap(amount) });
      }
    }
    return rows.sort((a, b) => a.year.localeCompare(b.year) || a.scope.localeCompare(b.scope));
  }

  get calcRows(): FlagRow[] {
    const R = 'program_builder.readout';
    const byCredit = this.bool('PRE_CALC_REQUESTED_TOTAL_BY_CREDIT');
    const byPercent = this.bool('PRE_CALC_REQUESTED_TOTAL_BY_PERCENT');
    const byClaims = this.bool('CALCULATE_REIMBURSEMENT_BY_CLAIMS_FINANCIALS');
    const byGpa = this.bool('PRE_CALC_REQUESTED_TOTAL_BY_GPA');
    const pct = (name: string) => (this.str(name) ? `${this.str(name)}%` : '—');
    return [
      {
        name: 'PRE_CALC_REQUESTED_TOTAL_BY_CREDIT',
        label: this.intl.t(`${R}.calc_by_credit`),
        on: byCredit,
        state: this.yesNo(byCredit),
        detailName: byCredit ? 'REIMBURSEMENT_PER_COURSE_CREDIT' : '',
        detailLabel: byCredit ? this.intl.t(`${R}.rate_per_credit`) : '',
        detailValue: byCredit
          ? money(
              getFieldValue(this.args.fields, 'REIMBURSEMENT_PER_COURSE_CREDIT'),
              this.intl.primaryLocale
            )
          : '',
      },
      {
        name: 'PRE_CALC_REQUESTED_TOTAL_BY_PERCENT',
        label: this.intl.t(`${R}.calc_by_percent`),
        on: byPercent,
        state: this.yesNo(byPercent),
        detailName: byPercent ? 'REIMBURSEMENT_PERCENTAGE' : '',
        detailLabel: byPercent ? this.intl.t(`${R}.reimbursement_pct`) : '',
        detailValue: byPercent ? pct('REIMBURSEMENT_PERCENTAGE') : '',
      },
      {
        name: 'CALCULATE_REIMBURSEMENT_BY_CLAIMS_FINANCIALS',
        label: this.intl.t(`${R}.calc_by_claims`),
        on: byClaims,
        state: this.yesNo(byClaims),
        detailName: byClaims ? 'REIMBURSEMENT_PERCENTAGE_FOR_CLAIMS_FINANCIALS' : '',
        detailLabel: byClaims ? this.intl.t(`${R}.claims_pct`) : '',
        detailValue: byClaims ? pct('REIMBURSEMENT_PERCENTAGE_FOR_CLAIMS_FINANCIALS') : '',
      },
      {
        name: 'PRE_CALC_REQUESTED_TOTAL_BY_GPA',
        label: this.intl.t(`${R}.calc_by_gpa`),
        on: byGpa,
        state: this.yesNo(byGpa),
        detailName: '',
        detailLabel: '',
        detailValue: '',
      },
    ];
  }

  /** True when any calculation method is enabled — off, the panel collapses to a sentence. */
  get anyCalcOn(): boolean {
    return this.calcRows.some((row) => row.on);
  }

  get optionRows(): FlagRow[] {
    const R = 'program_builder.readout';
    const multipleInstance = this.bool('MULTIPLE_INSTANCE_PROGRAM');
    const voucher = this.bool('VOUCHER_PROGRAM');
    const beyondLimit = this.bool('ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT');
    const balanceMultiplier = this.bool('USE_BALANCE_MULTIPLIER_FOR_PROGRAM_LIMITS');
    return [
      {
        name: 'MULTIPLE_INSTANCE_PROGRAM',
        label: this.intl.t(`${R}.multiple_instance`),
        on: multipleInstance,
        state: this.yesNo(multipleInstance),
        detailName: '',
        detailLabel: '',
        detailValue: '',
      },
      {
        name: 'VOUCHER_PROGRAM',
        label: this.intl.t(`${R}.voucher_program`),
        on: voucher,
        state: this.yesNo(voucher),
        detailName: voucher ? 'VOUCHER_FILENAME' : '',
        detailLabel: voucher ? this.intl.t(`${R}.voucher_type`) : '',
        detailValue: voucher ? this.str('VOUCHER_FILENAME') || '—' : '',
      },
      {
        name: 'ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT',
        label: this.intl.t(`${R}.beyond_limit`),
        on: beyondLimit,
        state: this.yesNo(beyondLimit),
        detailName: '',
        detailLabel: '',
        detailValue: '',
      },
      {
        name: 'USE_BALANCE_MULTIPLIER_FOR_PROGRAM_LIMITS',
        label: this.intl.t(`${R}.balance_multiplier`),
        on: balanceMultiplier,
        state: this.yesNo(balanceMultiplier),
        detailName: balanceMultiplier ? 'LOCK_BALANCE_MULTIPLIER_AT_START_OF_FISCAL_YEAR' : '',
        detailLabel: balanceMultiplier ? this.intl.t(`${R}.lock_balance_multiplier`) : '',
        detailValue: balanceMultiplier
          ? this.bool('LOCK_BALANCE_MULTIPLIER_AT_START_OF_FISCAL_YEAR')
            ? this.intl.t('program_builder.summary.value.on')
            : this.intl.t('program_builder.summary.value.off')
          : '',
      },
    ];
  }

  <template>
    <div class="space-y-5" ...attributes>
      <SectionPanel
        @icon={{PhMoney}}
        @title={{t "program_builder.readout.budget_limits_title"}}
        @iconColor="violet"
      >
        <:body>
          <div class="tio-row-between gap-3 text-sm">
            <span class="tio-fg-muted">
              {{t "program_builder.readout.limit_capacity"}}
              {{#if @showFieldNames}}
                <code class="ml-1 font-mono tio-text-micro tio-fg-brand">LIMIT_CAPACITY</code>
              {{/if}}
            </span>
            <span class="font-medium">{{this.capacityValue}}</span>
          </div>

          {{#if this.hasAnyLimit}}
            {{! Keep fixed-width columns scrolling inside the table wrapper. }}
            <div class="mt-3 overflow-x-auto">
              <table class="w-full text-sm" data-test-readout-limits-table>
                <thead>
                  <tr class="tio-text-overline border-b tio-border-subtle text-left tio-fg-muted">
                    <th scope="col" class="py-1 pr-3 font-medium">
                      {{t "program_builder.readout.limits_scope_column"}}
                    </th>
                    <th scope="col" class="w-40 py-1 pr-3 text-right font-medium">
                      {{t "program_builder.readout.limits_annual_column"}}
                      {{#if @showFieldNames}}
                        <code
                          class="block font-mono normal-case tio-text-micro tio-fg-brand"
                        >MAXIMUM_ANNUAL_REIMBURSEMENT</code>
                      {{/if}}
                    </th>
                    <th scope="col" class="w-40 py-1 text-right font-medium">
                      {{t "program_builder.readout.limits_lifetime_column"}}
                      {{#if @showFieldNames}}
                        <code
                          class="block font-mono normal-case tio-text-micro tio-fg-brand"
                        >MAXIMUM_LIFETIME_REIMBURSEMENT</code>
                      {{/if}}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {{#each this.limitRows as |row|}}
                    <tr
                      class="border-b tio-border-subtle last:border-0"
                      data-test-readout-limits-row={{row.scope}}
                    >
                      <td class="py-1.5 pr-3">{{row.scope}}</td>
                      <td
                        class="py-1.5 pr-3 text-right font-medium
                          {{if (eq row.annual '—') 'tio-fg-faint'}}"
                      >{{row.annual}}</td>
                      <td
                        class="py-1.5 text-right font-medium
                          {{if (eq row.lifetime '—') 'tio-fg-faint'}}"
                      >{{row.lifetime}}</td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>

            {{#if this.fiscalYearRows.length}}
              <div class="tio-text-overline mt-4 tio-fg-muted">
                {{t "program_builder.readout.fiscal_year_heading"}}
                {{#if @showFieldNames}}
                  <code
                    class="ml-1 font-mono normal-case tio-text-micro tio-fg-brand"
                  >MAXIMUM_ANNUAL_REIMBURSEMENT_BY_FISCAL_YEAR</code>
                {{/if}}
              </div>
              <div class="mt-1 overflow-x-auto">
                <table class="w-full text-sm" data-test-readout-fiscal-year-table>
                  <thead>
                    <tr class="tio-text-overline border-b tio-border-subtle text-left tio-fg-muted">
                      <th scope="col" class="w-24 py-1 pr-3 font-medium">
                        {{t "program_builder.readout.limits_fiscal_year_column"}}
                      </th>
                      <th scope="col" class="py-1 pr-3 font-medium">
                        {{t "program_builder.readout.limits_scope_column"}}
                      </th>
                      <th scope="col" class="w-40 py-1 text-right font-medium">
                        {{t "program_builder.readout.limits_annual_column"}}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {{#each this.fiscalYearRows as |row|}}
                      <tr
                        class="border-b tio-border-subtle last:border-0"
                        data-test-readout-fiscal-year-row="{{row.year}}-{{row.scope}}"
                      >
                        <td class="py-1.5 pr-3">{{row.year}}</td>
                        <td class="py-1.5 pr-3">{{row.scope}}</td>
                        <td
                          class="py-1.5 text-right font-medium
                            {{if (eq row.annual '—') 'tio-fg-faint'}}"
                        >{{row.annual}}</td>
                      </tr>
                    {{/each}}
                  </tbody>
                </table>
              </div>
            {{/if}}
          {{else}}
            {{! Mirrors the validation warning's language — an all-dash table
              would misread as "not configured yet" rather than a decision. }}
            <p class="mb-0 mt-3 text-sm tio-fg-muted" data-test-readout-no-limits>
              {{t "program_builder.readout.no_limits"}}
            </p>
          {{/if}}
        </:body>
      </SectionPanel>

      <SectionPanel
        @icon={{PhCalculator}}
        @title={{t "program_builder.readout.calculation_title"}}
        @iconColor="violet"
      >
        <:body>
          {{#if this.anyCalcOn}}
            <div class="space-y-1">
              {{#each this.calcRows as |row|}}
                <div
                  class="tio-row-between gap-3 py-0.5 text-sm"
                  data-test-readout-calc-row={{row.name}}
                >
                  <span class="tio-fg-muted">
                    {{row.label}}
                    {{#if @showFieldNames}}
                      <code class="ml-1 font-mono tio-text-micro tio-fg-brand">{{row.name}}</code>
                    {{/if}}
                  </span>
                  <span class="text-right font-medium">{{row.state}}</span>
                </div>
                {{#if row.detailLabel}}
                  <div class="tio-row-between gap-3 py-0.5 pl-4 text-sm">
                    <span class="tio-fg-muted">
                      {{row.detailLabel}}
                      {{#if @showFieldNames}}
                        <code
                          class="ml-1 font-mono tio-text-micro tio-fg-brand"
                        >{{row.detailName}}</code>
                      {{/if}}
                    </span>
                    <span
                      class="text-right font-medium {{if (eq row.detailValue '—') 'tio-fg-faint'}}"
                    >{{row.detailValue}}</span>
                  </div>
                {{/if}}
              {{/each}}
            </div>
          {{else}}
            <p class="mb-0 text-sm tio-fg-muted" data-test-readout-no-calculation>
              {{t "program_builder.readout.no_calculation"}}
            </p>
          {{/if}}
        </:body>
      </SectionPanel>

      <SectionPanel
        @icon={{PhToggleRight}}
        @title={{t "program_builder.readout.program_options_title"}}
        @iconColor="violet"
      >
        <:body>
          <div class="space-y-1">
            {{#each this.optionRows as |row|}}
              <div
                class="tio-row-between gap-3 py-0.5 text-sm"
                data-test-readout-option-row={{row.name}}
              >
                <span class="tio-fg-muted">
                  {{row.label}}
                  {{#if @showFieldNames}}
                    <code class="ml-1 font-mono tio-text-micro tio-fg-brand">{{row.name}}</code>
                  {{/if}}
                </span>
                <span class="text-right font-medium">{{row.state}}</span>
              </div>
              {{#if row.detailLabel}}
                <div class="tio-row-between gap-3 py-0.5 pl-4 text-sm">
                  <span class="tio-fg-muted">
                    {{row.detailLabel}}
                    {{#if @showFieldNames}}
                      <code
                        class="ml-1 font-mono tio-text-micro tio-fg-brand"
                      >{{row.detailName}}</code>
                    {{/if}}
                  </span>
                  <span
                    class="text-right font-medium {{if (eq row.detailValue '—') 'tio-fg-faint'}}"
                  >{{row.detailValue}}</span>
                </div>
              {{/if}}
            {{/each}}
          </div>
        </:body>
      </SectionPanel>
    </div>
  </template>
}
